import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Check, ArrowRight, Sparkles, Share2, Wallet, Link2, BarChart3, Percent,
  Infinity as InfinityIcon, Palette, Globe, Star, Megaphone,
} from "lucide-react";
import { toast } from "sonner";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";

const SUB_FEE = 12; // monthly subscription
const RATE = 0.075; // 7.5%
const PER_USER = SUB_FEE * RATE; // £0.90 recurring per referred user per month

const gbp = (n) => "£" + Math.round(n).toLocaleString("en-GB");

const TRUST_BADGES = [
  "Earn 7.5% of subscriptions, recurring for the life of every user you refer",
  "Minimum 5,000 followers, open to creators worldwide",
  "Ready made creative assets and a real time earnings dashboard",
];

const STREAMS = [
  {
    icon: Share2,
    title: "Referral commission",
    desc: "Share your unique code. Earn 7.5% of every subscription you refer, every single month, for as long as that user stays with Senior Stylist. Your income compounds as your referrals grow.",
  },
  {
    icon: Star,
    title: "Brand Ambassador",
    desc: "If you are a qualified stylist, register and verify to become a Brand Ambassador, earning from your own paid consultations on top of your referral commission.",
  },
];

const FEATURES = [
  { icon: InfinityIcon, title: "Lifetime recurring commission", desc: "This is not a one off bounty. You keep earning 7.5% of a referred user's subscription every month, for the entire life of that user." },
  { icon: BarChart3, title: "Real time earnings dashboard", desc: "Track sign ups, active subscribers and your monthly recurring income live, so you always know exactly what you are earning." },
  { icon: Link2, title: "Unique trackable code and link", desc: "Every sign up from your code or link is automatically attributed to you, with full transparency and no manual tracking." },
  { icon: Palette, title: "Ready made creative assets", desc: "Post with professionally designed, on brand content built for reels, stories and posts, so you can start promoting in minutes." },
  { icon: Wallet, title: "Transparent Stripe payouts", desc: "Earnings are credited automatically and paid via Stripe Connect. No invoicing, no chasing, no chargeback risk." },
  { icon: Percent, title: "Simple flat commission", desc: "Every affiliate earns the same 7.5%. No confusing tiers, no thresholds to unlock and no rank to climb, just a straightforward recurring rate on everyone you refer." },
];

const STEPS = [
  { n: "01", icon: Megaphone, title: "Apply", desc: "Submit your details and social handles. Open worldwide to creators with 5,000 or more followers." },
  { n: "02", icon: Link2, title: "Get your code", desc: "Once approved, receive your unique trackable referral code and link inside the app." },
  { n: "03", icon: Share2, title: "Share with your audience", desc: "Post using your ready made creative assets and your personal code across your channels." },
  { n: "04", icon: Wallet, title: "Earn for life", desc: "Collect 7.5% of every subscription you refer, recurring every month, paid straight to you via Stripe." },
];

const ELIGIBILITY = [
  { title: "5,000+ followers", desc: "A minimum combined following of 5,000 across your main social platform to apply." },
  { title: "Open worldwide", desc: "Creators anywhere in the world can apply and start earning recurring commission." },
  { title: "Quick application", desc: "Submit your details and handles for a fast approval, then your code is ready to share." },
  { title: "Optional Brand Ambassador", desc: "Qualified stylists can verify to also earn from their own consultations as a Brand Ambassador." },
];

const FAQS = [
  { q: "What does 'for the life of the user' mean?", a: "You keep earning 7.5% of a referred user's subscription every month for as long as they stay subscribed, not just a one off payment at sign up. The longer they stay, the more you earn." },
  { q: "How much can I realistically earn?", a: "It depends on your audience size and how well it converts. Use the calculator above to estimate it. Because commission is recurring, your monthly income compounds as you refer more subscribers over time." },
  { q: "When and how do I get paid?", a: "Earnings are credited automatically and paid out via Stripe Connect once you reach the payout threshold, with full visibility in your dashboard." },
  { q: "Can I earn from consultations too?", a: "Yes. If you are a qualified stylist, register and verify to become a Brand Ambassador and earn from your own paid consultations on top of your referral income." },
];

const reveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

function EarningsCalculator() {
  const [perMonth, setPerMonth] = useState(60); // new referred subscribers per month
  const data = Array.from({ length: 12 }, (_, i) => {
    const m = i + 1;
    return { month: `M${m}`, income: Math.round(perMonth * m * PER_USER) };
  });
  const month12 = perMonth * 12 * PER_USER;
  const yearTotal = perMonth * PER_USER * 78; // sum of 1..12

  return (
    <div className="rounded-3xl border border-white/12 bg-white/[0.03] p-8 sm:p-10" data-testid="earnings-calculator">
      <div className="max-w-md mx-auto mb-8 text-center">
        <div className="flex items-center justify-between text-sm text-white/60">
          <span>New referred subscribers per month</span>
          <span className="font-mono text-stylist-gold text-base">{perMonth}</span>
        </div>
        <input type="range" min="5" max="500" step="5" value={perMonth} data-testid="calc-permonth"
          onChange={(e) => setPerMonth(Number(e.target.value))}
          className="mt-3 w-full accent-[#C59B27] cursor-pointer" />
        <p className="mt-2 text-xs text-white/40">
          Based on the £12 monthly membership, which members pay plus VAT, at 7.5% recurring commission on the net £12. Earnings compound as referrals stay subscribed.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        <div className="rounded-2xl bg-stylist-gold/[0.08] border border-stylist-gold/25 p-5 text-center">
          <p className="text-[10px] font-mono uppercase tracking-widest text-stylist-gold">Recurring by month 12</p>
          <p className="mt-1 font-serif text-4xl text-stylist-gold tabular-nums" data-testid="calc-month12">{gbp(month12)}</p>
          <p className="text-xs text-white/45">per month</p>
        </div>
        <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5 text-center">
          <p className="text-[10px] font-mono uppercase tracking-widest text-white/50">Total earned, year one</p>
          <p className="mt-1 font-serif text-4xl text-white tabular-nums" data-testid="calc-year">{gbp(yearTotal)}</p>
          <p className="text-xs text-white/45">and growing every month</p>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={240}>
        <AreaChart data={data} margin={{ left: 0, right: 8 }}>
          <defs>
            <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#C59B27" stopOpacity={0.55} />
              <stop offset="100%" stopColor="#C59B27" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#ffffff10" vertical={false} />
          <XAxis dataKey="month" stroke="#8C857B" fontSize={11} />
          <YAxis stroke="#8C857B" fontSize={11} tickFormatter={(v) => "£" + v} width={54} />
          <Tooltip formatter={(v) => [gbp(v) + " / mo", "Recurring income"]}
            contentStyle={{ background: "#141416", border: "1px solid #ffffff20", borderRadius: 12, color: "#fff" }} />
          <Area type="monotone" dataKey="income" stroke="#C59B27" strokeWidth={2.5} fill="url(#goldFill)" />
        </AreaChart>
      </ResponsiveContainer>
      <p className="text-center text-xs text-white/40 mt-3">Projected monthly recurring commission as your referrals accumulate over the first year.</p>
    </div>
  );
}

export default function InfluencerLanding() {
  const [showBar, setShowBar] = useState(false);

  useEffect(() => {
    document.title = "Become an Affiliate | Senior Stylist";
    window.scrollTo(0, 0);
    const onScroll = () => setShowBar(window.scrollY > 640);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const notOpen = () =>
    toast("We are not onboarding influencers at the moment", {
      description: "We will keep you posted. Please check back soon.",
    });

  return (
    <div className="bg-stylist-ink text-white" data-testid="influencer-landing-page">
      <header className="sticky top-0 z-40 backdrop-blur bg-stylist-ink/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="influencer-logo">
            <span className="w-9 h-9 rounded-full bg-white flex items-center justify-center">
              <img src="/logo.png" alt="Senior Stylist" className="w-6 h-6 object-contain" />
            </span>
            <span className="font-serif text-lg">Senior Stylist</span>
          </Link>
          <button onClick={notOpen} data-testid="header-cta"
            className="rounded-full bg-stylist-gold text-stylist-ink px-5 py-2.5 text-xs font-medium hover:bg-white transition-colors">
            Become an affiliate
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden pt-16 pb-20 md:pt-24 md:pb-28">
        <div className="absolute inset-0 ss-grain opacity-[0.06] pointer-events-none" />
        <div className="absolute inset-0 opacity-70 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 20% 0%, rgba(197,155,39,0.22), transparent 55%), radial-gradient(ellipse at 90% 20%, rgba(197,155,39,0.10), transparent 50%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="font-mono text-[0.62rem] sm:text-[0.68rem] uppercase tracking-[0.24em] text-stylist-gold">
              For creators · recurring commission · worldwide
            </p>
            <h1 className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.03] tracking-tight text-white">
              Turn your audience into{" "}
              <span className="italic text-stylist-gold">lifetime income.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/75 max-w-xl leading-relaxed">
              Recommend real expert hair consultations to your followers and earn 7.5% of every
              subscription you refer, recurring every month for the life of that user. Not a one off
              payout, a growing income that compounds with your audience.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button onClick={notOpen} data-testid="hero-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-stylist-gold text-stylist-ink px-8 py-4 text-sm font-medium hover:bg-white transition-colors duration-300">
                Become an affiliate
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => scrollTo("calculator")} data-testid="hero-see-how"
                className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors">
                <Sparkles className="w-4 h-4 text-stylist-gold" />
                See your earning potential
              </button>
            </div>
            <ul className="mt-10 space-y-2.5">
              {TRUST_BADGES.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-white/80">
                  <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-stylist-gold/15 flex items-center justify-center">
                    <Check className="w-3 h-3 text-stylist-gold" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </div>

          {/* creator earnings mockup */}
          <div className="lg:col-span-6">
            <div className="relative mx-auto max-w-[420px]">
              <div className="absolute -inset-6 rounded-[36px] bg-stylist-gold/15 blur-3xl pointer-events-none" />
              <div className="relative rounded-[28px] border border-white/12 bg-[#141416] shadow-2xl p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className="w-10 h-10 rounded-full bg-stylist-gold/20 flex items-center justify-center text-stylist-gold font-serif">@</span>
                    <div className="leading-tight">
                      <p className="text-sm font-medium text-white">@yourhandle</p>
                      <p className="text-[10px] font-mono uppercase tracking-wider text-white/40">Affiliate creator</p>
                    </div>
                  </div>
                  <span className="rounded-full bg-stylist-gold/15 text-stylist-gold text-[10px] font-mono uppercase tracking-wider px-3 py-1.5">7.5%</span>
                </div>
                <div className="mt-6 rounded-2xl bg-white/[0.04] border border-white/10 p-5 text-center">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-white/40">Monthly recurring</p>
                  <p className="mt-1 font-serif text-4xl text-stylist-gold">£742</p>
                  <p className="text-xs text-white/45">and growing every month</p>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-white/10 p-3">
                    <p className="font-serif text-2xl text-white">824</p>
                    <p className="text-[11px] text-white/45">active subscribers</p>
                  </div>
                  <div className="rounded-xl border border-white/10 p-3">
                    <p className="font-serif text-2xl text-stylist-gold">£4,120</p>
                    <p className="text-[11px] text-white/45">earned to date</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TWO WAYS TO EARN */}
      <section className="py-16 md:py-24 bg-[#141416]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <motion.div {...reveal} className="text-center max-w-2xl mx-auto mb-14">
            <span className="font-mono text-[0.68rem] uppercase tracking-[0.28em] text-stylist-gold">Ways to earn</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl">Recommend, refer, repeat</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {STREAMS.map((s, i) => (
              <motion.div key={s.title} {...reveal} transition={{ ...reveal.transition, delay: i * 0.1 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 hover:border-stylist-gold/40 transition-colors duration-500"
                data-testid={`stream-${i + 1}`}>
                <div className="w-12 h-12 rounded-full bg-stylist-gold/15 flex items-center justify-center">
                  <s.icon className="w-6 h-6 text-stylist-gold" />
                </div>
                <h3 className="mt-5 font-serif text-2xl">{s.title}</h3>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <section id="calculator" className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-16">
          <motion.div {...reveal} className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-mono text-[0.68rem] uppercase tracking-[0.28em] text-stylist-gold">Earnings estimator</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl">See your earning potential</h2>
            <p className="mt-4 text-white/55">
              Because your commission is recurring, every new subscriber stacks on top of the last. Drag to see how it compounds.
            </p>
          </motion.div>
          <motion.div {...reveal}>
            <EarningsCalculator />
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 md:py-24 bg-[#141416]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <motion.div {...reveal} className="text-center max-w-2xl mx-auto mb-14">
            <span className="font-mono text-[0.68rem] uppercase tracking-[0.28em] text-stylist-gold">Live in minutes</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl">Four steps to recurring income</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <motion.div key={s.title} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-stylist-gold/40 transition-colors duration-500"
                data-testid={`step-${s.n}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs tracking-[0.3em] text-white/40">{s.n}</span>
                  <div className="w-10 h-10 rounded-full bg-stylist-gold/15 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-stylist-gold" />
                  </div>
                </div>
                <h3 className="mt-4 font-serif text-lg leading-snug">{s.title}</h3>
                <p className="mt-2 text-sm text-white/55 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <motion.div {...reveal} className="max-w-2xl mb-14">
            <span className="font-mono text-[0.68rem] uppercase tracking-[0.28em] text-stylist-gold">Built for creators</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl">Everything you need to earn</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <motion.div key={f.title} {...reveal} transition={{ ...reveal.transition, delay: (i % 3) * 0.08 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-7 hover:border-stylist-gold/40 hover:bg-white/[0.05] transition-all duration-500"
                data-testid={`feature-${i + 1}`}>
                <div className="w-12 h-12 rounded-full bg-stylist-gold/15 flex items-center justify-center">
                  <f.icon className="w-6 h-6 text-stylist-gold" />
                </div>
                <h3 className="mt-5 font-serif text-xl leading-snug">{f.title}</h3>
                <p className="mt-3 text-sm text-white/55 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ELIGIBILITY */}
      <section className="py-16 md:py-24 bg-[#141416]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <motion.div {...reveal} className="lg:col-span-4">
              <span className="font-mono text-[0.68rem] uppercase tracking-[0.28em] text-stylist-gold">Who can apply</span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">Eligibility</h2>
              <div className="mt-8 rounded-2xl border border-white/12 bg-white/[0.04] p-7 flex items-center gap-4">
                <span className="w-12 h-12 shrink-0 rounded-full bg-stylist-gold/15 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-stylist-gold" />
                </span>
                <p className="text-sm text-white/60">
                  <span className="text-stylist-gold font-medium">5,000+ followers</span>, open to creators worldwide.
                </p>
              </div>
            </motion.div>
            <div className="lg:col-span-8 grid gap-5">
              {ELIGIBILITY.map((e, i) => (
                <motion.div key={e.title} {...reveal} transition={{ ...reveal.transition, delay: i * 0.07 }}
                  className="flex items-start gap-5 rounded-2xl border border-white/10 bg-white/[0.03] p-7"
                  data-testid={`eligibility-${i + 1}`}>
                  <span className="mt-0.5 w-6 h-6 shrink-0 rounded-full bg-stylist-gold/20 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-stylist-gold" />
                  </span>
                  <div>
                    <h3 className="font-serif text-xl">{e.title}</h3>
                    <p className="mt-1.5 text-sm text-white/55 leading-relaxed">{e.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <motion.div {...reveal} className="text-center mb-10">
            <span className="font-mono text-[0.68rem] uppercase tracking-[0.28em] text-stylist-gold">Good to know</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl">Creator questions, answered</h2>
          </motion.div>
          <motion.div {...reveal}>
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} className="border-white/10">
                  <AccordionTrigger className="font-serif text-lg text-white hover:no-underline hover:text-stylist-gold py-5 text-left">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/60 leading-relaxed text-sm">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 ss-grain opacity-[0.06] pointer-events-none" />
        <div className="absolute inset-0 opacity-60 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 50% 120%, rgba(197,155,39,0.22), transparent 60%)" }} />
        <motion.div {...reveal} className="relative max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Your followers already trust your taste. Get paid for it, every month.
          </h2>
          <p className="mt-6 text-lg text-white/60 max-w-xl mx-auto">
            Join as an affiliate, share your code, and build a recurring income that grows with your audience.
          </p>
          <div className="mt-10 flex justify-center">
            <button onClick={notOpen} data-testid="bottom-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-stylist-gold text-stylist-ink px-9 py-4 text-sm font-medium hover:bg-white transition-colors duration-300">
              Become an affiliate
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
          <p className="mt-10 text-[11px] font-mono uppercase tracking-[0.22em] text-white/40">
            Senior Stylist Ltd. · 7.5% recurring commission · Payouts via Stripe Connect
          </p>
        </motion.div>
      </section>

      {/* STICKY BAR */}
      <div className={"fixed bottom-0 inset-x-0 z-40 transition-transform duration-500 " + (showBar ? "translate-y-0" : "translate-y-full")}
        data-testid="sticky-cta-bar">
        <div className="mx-auto max-w-3xl m-3 rounded-2xl bg-[#141416] border border-white/12 shadow-2xl px-5 py-3 flex items-center justify-between gap-4">
          <div className="leading-tight">
            <p className="text-sm font-medium text-white">Become an affiliate</p>
            <p className="text-xs text-white/50">7.5% recurring, for the life of every user</p>
          </div>
          <button onClick={notOpen} data-testid="sticky-cta-button"
            className="shrink-0 rounded-full bg-stylist-gold text-stylist-ink px-6 py-3 text-sm font-medium hover:bg-white transition-colors">
            Apply now
          </button>
        </div>
      </div>
    </div>
  );
}
